import differenceInYears from 'date-fns/differenceInYears';
import React, { useMemo } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { useCompleteFragment } from '@eluve/apollo-client';
import { HStack, NewButton, VStack, legacyTextStyles } from '@eluve/components';
import { formatDate } from '@eluve/date-utils';
import { patientFragment } from '@eluve/frontend-appointment-hooks';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';
import { TabLink, TabNav, useIsFeatureFlagEnabled } from '@eluve/smart-blocks';
import { formatHumanName } from '@eluve/utils';
import { getVendorDataMapper } from '@eluve/vendor';

const baseTabs: TabLink[] = [
  {
    name: 'Appointments',
    link: 'appointments',
  },
  {
    name: 'Imported Notes',
    link: 'imported-notes',
  },
];

export interface PatientDetailsPageProps {}

export const PatientDetailsPage: React.FC<PatientDetailsPageProps> = () => {
  const { patientId } = useParams() as {
    patientId: string;
  };

  const patientDetails = useCompleteFragment({
    fragment: patientFragment,
    key: {
      id: patientId,
    },
  });

  const isUserInsightsChatEnabled =
    useIsPostHogFeatureFlagEnabled('INSIGHTS_CHAT');
  const isTenantInsightsChatEnabled = useIsFeatureFlagEnabled('INSIGHTS_CHAT');
  const isInsightsChatEnabled =
    isUserInsightsChatEnabled || isTenantInsightsChatEnabled;

  const tabLinks = useMemo(() => {
    if (!isInsightsChatEnabled) {
      return baseTabs;
    }

    return [{ name: 'Artifacts', link: 'artifacts' }, ...baseTabs];
  }, [isInsightsChatEnabled]);

  const name = formatHumanName(
    patientDetails?.firstName,
    patientDetails?.lastName,
  );
  const initials = `${patientDetails?.firstName?.charAt(0) ?? ''}${patientDetails?.lastName?.charAt(0) ?? ''}`;
  const dob = patientDetails.dateOfBirth
    ? formatDate.toDisplay(patientDetails.dateOfBirth)
    : null;
  const age = patientDetails?.dateOfBirth
    ? differenceInYears(new Date(), new Date(patientDetails.dateOfBirth))
    : null;

  const externalPatientInfos = patientDetails.external_patients_info ?? [];

  return (
    <VStack gap={8} className="py-3 md:p-8" wFull>
      {/* Patient Details */}
      <HStack gap={6}>
        <div
          className={`flex aspect-square size-16 items-center justify-center rounded-full bg-text-primary leading-none text-white privacy-text md:size-24 ${legacyTextStyles.heading({ size: 'm' })}`}
        >
          {initials}
        </div>

        <VStack>
          <VStack>
            <p
              className={`privacy-text ${legacyTextStyles.title({ size: 'l' })}`}
            >
              {name}
            </p>
            {externalPatientInfos
              .filter((info) => info.external_ehr?.vendor)
              .map((info) => (
                <HStack key={info.id}>
                  <NewButton
                    type="outline"
                    text={'Copy External ID'}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(info.externalPatientId);
                    }}
                    size="s"
                  />
                  <NewButton
                    type="outline"
                    text={`View on ${info.external_ehr.vendor}`}
                    onClick={() => {
                      const vendorProvider = getVendorDataMapper(
                        info.external_ehr.vendor,
                      );
                      const url = vendorProvider.getPatientEhrUrl({
                        domain: info.external_ehr.domain,
                        externalPatientId: info.externalPatientId,
                      });
                      window.open(url, '_blank', 'noopener,noreferrer');
                    }}
                    size="s"
                  />
                </HStack>
              ))}
          </VStack>

          <VStack gap={0}>
            {dob && (
              <p
                className={`privacy-text ${legacyTextStyles.body({ size: 'm' })}`}
              >
                Date of Birth: <span className="font-bold">{dob}</span>
              </p>
            )}
            {age && (
              <p
                className={`privacy-text ${legacyTextStyles.body({ size: 'm' })}`}
              >
                Age: <span className="font-bold">{age} years old</span>
              </p>
            )}
          </VStack>
        </VStack>
      </HStack>

      <VStack wFull>
        {/* Tab View */}
        <TabNav tabs={tabLinks} />
        <Outlet />
      </VStack>
    </VStack>
  );
};
